import { ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PartFamily as PartFamilyType } from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";

const PAGE_SIZE = 10;

const PartFamily = () => {
  const navigate = useNavigate();
  const [partFamilies, setPartFamilies] = useState<PartFamilyType[]>([]);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPartFamilies = useCallback(async (pageNumber: number) => {
    setLoading(true);
    setError(null);

    try {
      const families = await AllPartApi.api.getPartFamilies(
        pageNumber,
        PAGE_SIZE
      );

      if (pageNumber > 0 && families.length === 0) {
        setHasNextPage(false);
        return;
      }

      setPartFamilies(families);
      setPage(pageNumber);
      setHasNextPage(families.length === PAGE_SIZE);
    } catch (fetchError) {
      console.error("Error loading part families", fetchError);
      setError("Failed to load part families. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPartFamilies(0);
  }, [fetchPartFamilies]);

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="w-64 flex-shrink-0 bg-white shadow-md">
        <SideBarNavigation />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-[#F8FAFC] min-w-0">
          <div className="p-4 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{ textTransform: "none" }}>
                Back
              </Button>
              <h1 className="text-2xl font-semibold text-left">Part Family</h1>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] bg-white">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {["Part Family ID", "Part Family", "Part Location"].map(
                        (heading) => (
                          <th
                            key={heading}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-10 text-center">
                          <CircularProgress size={24} aria-label="Loading part families" />
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan={4} className="p-4">
                          <Alert severity="error">{error}</Alert>
                        </td>
                      </tr>
                    ) : partFamilies.length > 0 ? (
                      partFamilies.map((family, index) => (
                        <tr key={family.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 w-[100px] text-sm text-gray-900">
                            {family.id}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {family.partFamily}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {family.partLocation}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-10 text-center text-sm text-gray-500">
                          No part families found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {page > 0 || hasNextPage ? (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => void fetchPartFamilies(0)}
                      disabled={loading || page === 0}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50">
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() => void fetchPartFamilies(page - 1)}
                      disabled={loading || page === 0}
                      aria-label="Previous page"
                      className="px-3 py-2 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed">
                      &lt;
                    </button>
                    <button
                      type="button"
                      onClick={() => void fetchPartFamilies(page + 1)}
                      disabled={loading || !hasNextPage}
                      aria-label="Next page"
                      className="px-3 py-2 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed">
                      &gt;
                    </button>
                  </div>
                  <span className="text-sm text-gray-700">Page {page + 1}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartFamily;
